import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { VRMLoaderPlugin, VRMUtils } from '@pixiv/three-vrm';
import { createVRMAnimationClip, VRMAnimationLoaderPlugin } from '@pixiv/three-vrm-animation';

export class VrmAvatar {
    constructor(scene, camera) {
        this.confidenceHistory = new Map([
            ['aa', []],
            ['ih', []],
            ['ou', []],
            ['ee', []],
            ['oh', []],
        ]);
        this.scene = scene;
        this.camera = camera;

        // Model loader setup
        this.loader = new GLTFLoader();
        this.loader.register((parser) => new VRMLoaderPlugin(parser));
        this.loader.register((parser) => new VRMAnimationLoaderPlugin(parser));

        this.currentVrm = null;
        this.currentVrmAnimation = null;
        this.currentMixer = undefined;
        this.time = 0;
        this.update();
    }

    load(url) {
        this.loader.load(
            url,
            (gltf) => {
                this.initializeVRM(gltf);
                this.initializeVRMAnimation(gltf);
            },
            (progress) => {
                // console.log(
                //     `Loading... ${Math.round(100.0 * (progress.loaded / progress.total))}%`
                // );
            },
            (error) => console.error(error)
        );
    }

    async preloadGLTF(url) {
        return new Promise((resolve, reject)=>{
            this.loader.load(
                url,
                (gltf) => {
                    resolve(gltf);
                },
                (progress) => {
                    // console.log(
                    //     `Loading... ${Math.round(100.0 * (progress.loaded / progress.total))}%`
                    // );
                },
                (error) => reject(error)
            );
        });
    }

    get stillSpeeking() {
        return this.speekPromisePair !== undefined;
    }
    
    async speek(audioBlob, interrupt = false) {
        if(this.speekPromisePair && !interrupt) {
            this.speekPromisePair.reject("Still in speeking")
            return;
        }
        this.speekPromisePair = undefined;

        if (!this.audioContext) {
            this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
            this.analyser = this.audioContext.createAnalyser();
            this.analyser.fftSize = 2048;
            this.analyser.smoothingTimeConstant = 0.8;
        }

        try {
            // Convert Blob to ArrayBuffer
            const arrayBuffer = await audioBlob.arrayBuffer();
            const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
            
            if (this.source) this.source.stop();
            this.source = this.audioContext.createBufferSource();
            this.source.buffer = audioBuffer;
            this.source.connect(this.analyser);
            this.analyser.connect(this.audioContext.destination);
            this.source.start(0);
            this.source.addEventListener("ended", ()=> this.audioSourceEnd());
            this.analyze();
            //stop event delay to future frame will case the frequencyData get cleared, fix later
            return new Promise((resolve, reject)=>{
                this.speekPromisePair = {resolve, reject};
            });
        } catch (error) {
            console.error('Error loading audio:', error);
        }
    }

    stopSpeeking() {
        if (this.source) {
            this.source.stop();
            this.audioSourceEnd();
        }
    }

    audioSourceEnd() {
        this.frequencyData = null;
        if(this.speekPromisePair) {
            this.speekPromisePair.resolve();
            this.speekPromisePair = undefined;
        }
    }

    analyze() {
        this.frequencyData = new Uint8Array(this.analyser.frequencyBinCount);
    }

    process () {
        let confidences;
        if(this.audioContext && this.analyser && this.frequencyData)
        {
            this.analyser.getByteFrequencyData(this.frequencyData);
            const formants =  this.getFormants(this.audioContext, this.analyser, this.frequencyData);
            confidences =  this.detectState(formants);
        } else {
            confidences = {
                aa: 0,
                ih: 0,
                ou: 0,
                ee: 0,
                oh: 0
            };
        }
        if (this.currentVrm?.expressionManager) {
            // Set all expression weights
            Object.entries(confidences).forEach(([phoneme, weight]) => {
                this.currentVrm.expressionManager.setValue(phoneme, weight);
            });

            // Apply the weights immediately
            this.currentVrm.expressionManager.update();
        }
    };

    getFormants(audioContext, analyser, data) {
        const freqRes = audioContext.sampleRate / analyser.fftSize;
        return {
            f1: VrmAvatar.getPeak(data, 200, 800, freqRes),
            f2: VrmAvatar.getPeak(data, 800, 2400, freqRes)
        };
    }

    static getPeak(data, minFreq, maxFreq, res) {
        const start = Math.floor(minFreq / res);
        const end = Math.floor(maxFreq / res);
        let maxIndex = start;
        
        for (let i = start; i <= end; i++) {
            if (data[i] > data[maxIndex]) maxIndex = i;
        }
        return maxIndex * res;
    }

    detectState({f1, f2}) {
       const confidences = {
            aa: Math.max(0, 1 - Math.abs(f1 - 700) / 300) * Math.max(0, 1 - Math.abs(f2 - 1200) / 500),
            ih: Math.max(0, 1 - Math.abs(f1 - 400) / 200) * Math.max(0, 1 - Math.abs(f2 - 2200) / 400),
            ou: Math.max(0, 1 - Math.abs(f1 - 300) / 150) * Math.max(0, 1 - Math.abs(f2 - 900) / 300),
            ee: Math.max(0, 1 - Math.abs(f1 - 250) / 150) * Math.max(0, 1 - Math.abs(f2 - 2800) / 500),
            oh: Math.max(0, 1 - Math.abs(f1 - 500) / 200) * Math.max(0, 1 - Math.abs(f2 - 1500) / 400)
        };

        // Normalize confidences
        const maxConfidence = Math.max(...Object.values(confidences));
        const normalizedConfidences = Object.fromEntries(
            Object.entries(confidences).map(([key, value]) => [
                key,
                maxConfidence > 0 ? value / maxConfidence : 0
            ])
        );

        // Apply temporal smoothing
        const smoothedConfidences = {};
        for (const [phoneme, confidence] of Object.entries(confidences)) {
            this.confidenceHistory.get(phoneme).push(confidence);
            if (this.confidenceHistory.get(phoneme).length > 5) {
                this.confidenceHistory.get(phoneme).shift();
            }
            smoothedConfidences[phoneme] = this.confidenceHistory.get(phoneme)
                .reduce((a, b) => a + b, 0) / this.confidenceHistory.get(phoneme).length;
        }

        return smoothedConfidences;
    }

    initializeVRM(gltf, loadingPreview = true) {
        const vrm = gltf.userData.vrm;
        if (!vrm)
            return;

        // Cleanup previous VRM
        if (this.currentVrm) {
            if (this.currentMixer) this.currentMixer.stopAllAction();
            this.scene.remove(this.currentVrm.scene);
            VRMUtils.deepDispose(this.currentVrm.scene);
        }

        if(vrm.meta && vrm.meta.metaVersion === '1') {
            vrm.scene.rotation.y = Math.PI
        }
        if(loadingPreview){
            const mesh = vrm.scene.getObjectByName("Mesh", true);
            this.loadingMaterials = [];
            if(mesh instanceof THREE.SkinnedMesh) {
                mesh.material = new THREE.MeshStandardMaterial({
                    wireframe: true,
                    color: new THREE.Color().setHex( 0x1BE7B2 ),
                    emissive: new THREE.Color().setHex( 0x1BE7B2 ),
                    emissiveIntensity: 1,
                    alphaHash: true,
                    opacity: 0.5,
                });
                this.loadingMaterials.push(mesh.material);
            }
            mesh.children.forEach(e=>{
                if(e instanceof THREE.SkinnedMesh) {
                    e.material = new THREE.MeshStandardMaterial({
                        wireframe: true,
                        color: new THREE.Color().setHex( 0x1BE7B2 ),
                        emissive: new THREE.Color().setHex( 0x1BE7B2 ),
                        emissiveIntensity: 1,
                        alphaHash: true,
                        opacity: 0.5,
                        side: 1,
                    });
                    this.loadingMaterials.push(e.material);
                }
            })
            if(this.perviewVRM) {
                this.scene.remove(this.perviewVRM.scene);
                VRMUtils.deepDispose(this.perviewVRM.scene);
            }
            this.perviewVRM = vrm;
        } else {
            this.currentVrm = vrm;
        }
        this.scene.add(vrm.scene);
        this.initializeAnimations();
    }

    fadeOutPreview() {
        this.loadingMaterials = undefined;
        if (this.currentPerviewMixer) this.currentPerviewMixer.stopAllAction();
        if(this.perviewVRM) {
            this.scene.remove(this.perviewVRM.scene);
        }
    }

    initializeVRMAnimation(gltf) {
        const vrmAnimations = gltf.userData.vrmAnimations;
        if (!vrmAnimations) return;

        this.currentVrmAnimation = vrmAnimations[0];
        this.initializeAnimations();
    }

    initializeAnimations() {
        if (this.perviewVRM && this.currentVrmAnimation) {
            this.currentPerviewMixer = new THREE.AnimationMixer(this.perviewVRM.scene);
            const clip = createVRMAnimationClip(
                this.currentVrmAnimation,
                this.perviewVRM
            );
            this.currentPerviewMixer.clipAction(clip).play();
        }
        if (this.currentVrm && this.currentVrmAnimation) {
            this.currentMixer = new THREE.AnimationMixer(this.currentVrm.scene);
            const clip = createVRMAnimationClip(
                this.currentVrmAnimation,
                this.currentVrm
            );
            this.currentMixer.clipAction(clip).play();
        }
    }

    update(delta) {
        this.time += isNaN(delta) ? 0 : delta;

        if(this.loadingMaterials){
            if (this.currentPerviewMixer) this.currentPerviewMixer.update(delta);
            if (this.perviewVRM) this.perviewVRM.update(delta);
        }
        if (this.currentMixer) this.currentMixer.update(delta);
        if (this.currentVrm) this.currentVrm.update(delta);

        if(this.loadingMaterials) {
            this.loadingMaterials.forEach(e=>{
                e.opacity = (Math.sin(this.time) + 1) *.5 *.5;
                e.needsUpdate = true;
            })
        }
        this.process();
    }
}